<script lang="ts">
    import {onMount} from "svelte";
    import {getEmailTemplates, sendEmailTemplate} from "$lib/calls/mail";
    import {Button, Dropdown, DropdownItem, Input, Label, Modal, Select} from "flowbite-svelte";
    import Typeahead from "$lib/components/Typeahead.svelte";
    import {
        EnvelopeSolid,
    } from "flowbite-svelte-icons";
    import {globalToast} from "$lib/stores/toast";
    import {mailTemplates} from "$lib/stores/mailTemplates";
    import EmailTemplateEditor from "$lib/components/EmailTemplateEditor.svelte";
    import {allUnits} from "$lib/stores/unit";
    import type {EmailTemplate} from "$lib/types/EmailTemplate";
    import parseHandlebars, {findUnmatchedVariables} from "$lib/utils/templating/parseHandlebars";
    import type {ContractHistory} from "$lib/types/employee";
    import {contractTypes} from "$lib/utils/templating/contractTypes";
    import ModalHeader from "$lib/components/ui/ModalHeader.svelte";
    import ModalFooter from "$lib/components/ui/ModalFooter.svelte";
    import hasEmploymentStatus, {hasOneOfStati} from "$lib/utils/hasEmploymentStatus";
    import {mailTemplateLevels} from "$lib/utils/tableHeaders/mailTemplates";

    export let open = false;

    export let employee;

    let editedTemplate:EmailTemplate;

    let input = {}

    let memory:EmailTemplate|null = null;
    let contractHistory:ContractHistory|null = null;
    let email: {from: {fromEmail: string, fromName: string}, subject: string, template: any, variables: any[]} = {
        from: {
            fromEmail: 'legal@recrew.de',
            fromName: 'REcrew Legal',
        },
        subject: '',
        template: null,
        variables: []
    }

    let filteredTemplates:EmailTemplate[] = [];

    const definedFroms = [
        {name: 'Legal: legal@recrew.de', value: {fromEmail:'legal@recrew.de', fromName: 'REcrew Legal'}},
        {name: 'Dispo Munich: dispo.munich@recrew.de', value: {fromEmail:'dispo.munich@recrew.de', fromName: 'REcrew Dispo'}},
        {name: 'Dispo Berlin: dispo.berlin@recrew.de', value: {fromEmail:'dispo.berlin@recrew.de', fromName: 'REcrew Dispo'}},
        {name: 'HR: work@recrew.de', value: {fromEmail:'work@recrew.de', fromName: 'REcrew\'ting'}},
    ]

    const submitEmail = async () => {
        // console.log(contentParts[0].content.substring(contentParts[0].content.length - 150))
        try{
            const res = await sendEmailTemplate(email.template.id, {
                fromEmail: email.from.fromEmail,
                fromName: email.from.fromName,
                subject: email.subject,
                entityId: employee.id,
                content: editedTemplate.content.replace(/<% body %>/g, ''),
                toEmail: employee.email,
                toName: employee.name,
                variables: findUnmatchedVariables(editedTemplate.content)
            })
            open = false

            // reset
            reset()
            $globalToast.timed('E-Mail gesendet', 4000, EnvelopeSolid)
        } catch (e) {
            alert('Error: ' + e)
        }
    }

    const reloadRendering = () => {
        memory = email.template; email.template = null; setTimeout(() =>{email.template = memory},50)
    }
    const reset = () => {
        email = {
            from: {
                fromEmail: 'legal@recrew.de',
                fromName: 'REcrew Legal',
            },
            subject: '',
            template: null,
            variables: []
        }
    }

    const changeEmailTemplate = (template:EmailTemplate) => {
        email.template = null;
        if(template.subject && template?.subject.length>0){
            email.subject = parseHandlebars({...template, content: template?.subject}, {Employee: employee, Contract: contractHistory, Input: input, Unit: $allUnits.find(x => x.id === employee.unitId)})
        } else {
            email.subject = ''
        }
        setTimeout(() =>{email.template = template},400);
    }



    onMount(async() => {
        if($mailTemplates.length === 0){
            $mailTemplates = await getEmailTemplates()
        }
        const levels = mailTemplateLevels.filter(x => hasOneOfStati(employee, x.stati))
        filteredTemplates = $mailTemplates.filter(x => levels.map(y => y.value).includes(x.level))
    })

</script>


<Modal size="xl"
        title="E-Mail an {employee.name} verfassen"
        bind:open>
    <svelte:fragment slot="header"><ModalHeader title="E-Mail an {employee.name} verfassen"/></svelte:fragment>
    <form on:submit={submitEmail}>
        <div class="grid grid-cols-2 w-full gap-4">
            <Label class="space-y-2">
                <span>Von</span>
                <Select bind:value={email.from} items={definedFroms} required/>
            </Label>

            <Label class="space-y-2">
                <span>An</span>
                <div class="pt-2">{employee?.email}</div>
            </Label>
        </div>
        <div class="mt-3">
            <Typeahead
                    label="Vorlage"
                    value={email?.template?.name}
                    data={filteredTemplates}
                    extract={changeEmailTemplate}>
            </Typeahead>
        </div>
        {#if email.template}
            {#if email.template?.placeholders.find(x => x.entity === 'Contract')}
                <div class="mt-3">
                    <Label class="space-y-2">
                        <span>Vertragshistorie</span>
                        <Select bind:value={contractHistory} on:change={reloadRendering} items={
                        employee.contracts.map(x => x.history).flat()
                        .map(x => ({value: x, name: x.startDate.value.match(/\d{4}-\d{2}-\d{2}/)[0]  + ' - ' + (x.endDate.value ? x.endDate.value.match(/\d{4}-\d{2}-\d{2}/)[0] : 'unbefristet') + ' - ' + (contractTypes.find(y => y.value === x.historyType)?.name || 'Basisvertrag')}))} required/>
                    </Label>
                </div>
            {/if}
            {#each email.template?.placeholders as placeholder}
                {#if placeholder.entity === 'Input'}
                    <div class="mt-3">
                        <Label class="space-y-2">
                            <span>{placeholder.templateProperty}</span>
                            <Input on:blur={reloadRendering} required bind:value={input[placeholder.templateProperty]} type={placeholder.entityProperty}/>
                        </Label>
                    </div>
                {/if}
            {/each}
        {/if}
        <Label class="space-y-2 my-3">
            <span>Betreff</span>
            <Input required bind:value={email.subject} type="text"/>
        </Label>


        <div class="flex justify-between min-h-[500px]">
            <div class="w-4/5">
                {#if email.template}
                    <EmailTemplateEditor preview={true} on:update={(e) => {editedTemplate = e.detail; console.log({editedTemplate})}} template={email.template} fillData={{Employee: employee, Contract: contractHistory, Input: input, Unit: $allUnits.find(x => x.id === employee.unitId)}}/>

                    <!--{#each emailTemplateHandler(email.template) as part}
                        {#if part.type === 'render'}
                            {@html parseHandlebars({...email.template,content: part.content}, employee)}
                        {:else}
                            <TipTapEditor on:change={(e) => part.content = e.detail} content={parseHandlebars({...email.template,content: part.content}, employee)} />{/if}
                    {/each}-->
                {/if}
            </div>
            <div>
                <Button
                        on:click={() => alert('not yet implemented')}
                        color="alternative"
                        size="xs">
                    Anhang hinzufügen
                </Button>
                {#each email.variables as variable}
                    <Label class="space-y-2 my-3">
                        <span>{variable.variable}</span>
                        <Input required bind:value={variable.value} type="text" />
                    </Label>
                {/each}

            </div>

        </div>
    </form>
    <svelte:fragment slot="footer">
        <ModalFooter on:abort={() => {reset(); open = false}} on:next={submitEmail} nextText="Senden"/>

    </svelte:fragment>
</Modal>